/* Load on every storefront page, before page/form scripts. No pixel or network requests. */
(() => {
  'use strict';
  if (window.HuloAttribution?.version === 2) return;
  const HOSTS = new Set(['hulomedia.com', 'www.hulomedia.com']);
  if (location.protocol !== 'https:' || !HOSTS.has(location.hostname)) return;
  const KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id', 'campaign_id', 'adset_id', 'ad_id', 'placement', 'gclid', 'fbclid', 'msclkid', 'ttclid', 'gbraid', 'wbraid'];
  const CAMPAIGN_KEYS = KEYS.slice(0, 10);
  const UTM_KEYS = KEYS.slice(0, 6);
  const STORAGE_KEY = 'hulo_crm_attribution';
  const META_KEY = 'hulo_crm_attribution_meta';
  const TTL = 24 * 60 * 60 * 1000;
  const decorated = new Map();
  let selected = null, privacyWaits = 0;

  function clean(input) {
    const result = {};
    if (!input || typeof input !== 'object' || Array.isArray(input)) return result;
    for (const key of KEYS) {
      const value = input[key];
      if (typeof value === 'string' && value.trim()) result[key] = value.trim().slice(0, 500);
    }
    return result;
  }
  // UTM campaign labels travel in same-store URLs and inquiry form fields.
  // Browser attribution storage and click identifiers still require marketing consent.
  const entry = clean(Object.fromEntries(new URLSearchParams(location.search)));
  const hasEntry = Object.keys(entry).length > 0;
  const explicitCampaign = CAMPAIGN_KEYS.some(key => Boolean(entry[key]));
  function sameStore(url) {
    return url.protocol === 'https:' && HOSTS.has(url.hostname) && !url.port && !url.username && !url.password;
  }
  let internal = false;
  try { internal = sameStore(new URL(document.referrer)); } catch {}
  function permitted() {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return typeof privacy?.marketingAllowed === 'function' ? privacy.marketingAllowed() === true : null;
    } catch { return null; }
  }
  function clear() {
    selected = null;
    try { sessionStorage.removeItem(STORAGE_KEY); sessionStorage.removeItem(META_KEY); } catch {}
    for (const [link, record] of decorated) {
      if (link.getAttribute('href') === record.decorated) link.setAttribute('href', record.original);
    }
    decorated.clear();
  }
  function stored() {
    try {
      const meta = JSON.parse(sessionStorage.getItem(META_KEY) || 'null');
      if (meta && (meta.version !== 1 || !Number.isFinite(meta.recordedAt) || meta.recordedAt > Date.now() || Date.now() - meta.recordedAt >= TTL)) {
        sessionStorage.removeItem(STORAGE_KEY); sessionStorage.removeItem(META_KEY); return {};
      }
      return clean(JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}'));
    } catch { return {}; }
  }
  function read() {
    const allowed = permitted();
    if (allowed !== true) {
      if (allowed === false) clear();
      return Object.fromEntries(UTM_KEYS.filter(key => entry[key]).map(key => [key, entry[key]]));
    }
    if (!selected) {
      // Explicit campaign parameters or a fresh external click start a new visit.
      // A click ID on an internal link must not erase that visit's recorded UTMs.
      selected = explicitCampaign || (hasEntry && !internal) ? { ...entry } : { ...stored(), ...entry };
    }
    return { ...selected };
  }
  function remember() {
    const data = read();
    if (permitted() !== true) return data;
    if (Object.keys(data).length) {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        sessionStorage.setItem(META_KEY, JSON.stringify({ version: 1, recordedAt: Date.now() }));
      } catch {}
    }
    return data;
  }
  function waitForPrivacy() {
    if (permitted() === null && privacyWaits++ < 80) { setTimeout(waitForPrivacy, 100); return; }
    remember();
  }
  function preserveLink(event) {
    const link = event.target?.closest?.('a[href]');
    if (!link || link.hasAttribute('download')) return;
    const data = remember();
    if (!Object.keys(data).length) return;
    const original = link.getAttribute('href');
    if (!original || /^(?:#|mailto:|tel:|javascript:)/i.test(original)) return;
    let destination;
    try { destination = new URL(original, location.href); } catch { return; }
    if (!sameStore(destination) || /^\/(?:cart|checkout|checkouts|account|accounts|apps|admin|challenge)(?:\/|$)/i.test(destination.pathname)) return;
    // Leave deliberately tagged links alone; never mix their campaign with ours.
    if (CAMPAIGN_KEYS.some(key => Boolean(destination.searchParams.get(key)))) return;
    for (const key of KEYS) if (data[key] && !destination.searchParams.get(key)) destination.searchParams.set(key, data[key]);
    const next = destination.href;
    if (next !== link.href) {
      const prior = decorated.get(link);
      decorated.set(link, { original: prior?.original || original, decorated: next });
      link.setAttribute('href', next);
    }
  }

  window.HuloAttribution = Object.freeze({ version: 2, read, remember });
  document.addEventListener('visitorConsentCollected', remember);
  document.addEventListener('click', preserveLink, true);
  document.addEventListener('auxclick', preserveLink, true);
  document.addEventListener('contextmenu', preserveLink, true);
  window.addEventListener('pageshow', () => { privacyWaits = 0; waitForPrivacy(); });
  waitForPrivacy();
})();
